import { BadgeCard, BadgeCardWidth, ContextMenu, ContextMenuItem } from '@bp/ui-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useMemo } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import {
  useCreateTimetableCardsMutation,
  useDeleteTimetableCardsMutation,
} from '../../types/planung-graphql-client-defs';
import { createIntArray } from '../../utils/arrayFunc';
import { getBadgeCardColors } from '../../utils/badgeCardColors';
import { useTimetableStore } from '../TimetableGrid/TimetableProvider';
import { CardType } from '../TimetableVersion/graphql/types';
import styles from './PinboardCard.module.scss';

export interface PinboardCardProps {
  card: CardType;
  count?: number;
  passive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>, card: CardType) => void;
  className?: string | undefined;
  /** Überschreibt die aus der Dauer abgeleitete Breite — für Listen außerhalb des Rasters. */
  width?: BadgeCardWidth;
  /** Überschreibt die Beschriftung (sonst das Fachkürzel) — z.B. um Klassen und Lehrkräfte zu zeigen. */
  label?: string;
  /** Unterdrückt den nativen `title` – z.B. wenn ein externer Tooltip (ui-components) genutzt wird. */
  hideTitle?: boolean;
  /** Ablagestapel: Karte (`all = false`) oder den ganzen Stapel löschen. Ohne den Callback kein Löschen im Menü. */
  onDelete?: (all: boolean) => void;
}

export const PinboardCard: FC<PinboardCardProps> = observer(
  ({ card, count = 1, passive = false, onClick, className, width, label, hideTitle, onDelete }) => {
    const context = useMemorizedCacheTag('LESSONS');
    const [, createCards] = useCreateTimetableCardsMutation();
    const [, deleteCards] = useDeleteTimetableCardsMutation();

    const pinboardStore = useTimetableStore();

    const hasWarnings = card.warnings ? card.warnings.length > 0 : false;
    const hasAvailabilityWarnings = card.availabilityWarnings ? card.availabilityWarnings.length > 0 : false;
    const hasRooms = card.rooms.length !== 0;

    const convertToSingle = useCallback(async () => {
      let cardsToCreateCounter = 0;
      const cardsToDeleteUuids: string[] = [];

      if (card && card.duration && card.duration > 1) {
        cardsToDeleteUuids.push(card.uuid);
        cardsToCreateCounter = cardsToCreateCounter + card.duration;
      } else {
        return;
      }

      const versionUuid = pinboardStore.getCurrentVersion()?.uuid;
      const lessonUuid = card.lesson?.uuid;
      if (!versionUuid || !lessonUuid) return;

      const created = await createCards(
        {
          versionUuid,
          lessonUuid,
          durations: createIntArray(cardsToCreateCounter).map(() => 1),
          subjectContainerUuid: card.isSubjectContainer ? (card.subject?.uuid ?? null) : null,
        },
        context,
      );
      // Erst löschen, wenn die Einzelkarten angelegt sind: sonst gingen die Stunden verloren. Fehler
      // kommen hier als fehlende Daten an (der Client zeigt sie selbst an).
      if (!created.data?.createTimetableCards.length) return;

      await deleteCards({ cardUuids: cardsToDeleteUuids }, context);
      // Die neuen Karten kennt nur das Backend: Stecktafel neu laden.
      pinboardStore.reloadVersionData();
    }, [card, context, createCards, deleteCards, pinboardStore]);

    const contextMenu: ContextMenuItem[] = useMemo(() => {
      const menu: ContextMenuItem[] = [];

      menu.push({
        type: 'component',
        node: <div className={styles['menu-name']}>{card.subject?.name}</div>,
      });

      if (hasWarnings || hasAvailabilityWarnings || !hasRooms) {
        menu.push({
          type: 'ruler',
        });
      }

      if (hasWarnings) {
        menu.push({
          type: 'component',
          node: <></>,
        });
      }

      if (hasAvailabilityWarnings) {
        menu.push({
          type: 'component',
          node: <></>,
        });
      }

      if (!hasRooms) {
        menu.push({
          type: 'component',
          node: <div className={styles['menu-info']}>{t('pinboard.reason.noRoom')}</div>,
        });
      }

      menu.push(
        {
          type: 'ruler',
        },
        // {
        //   label: pinboardStore.pickedCard?.card === card ? t('pinboard.actions.unpick') : t('pinboard.actions.pick'),
        //   onClick: (e) => {
        //     e.stopPropagation();
        //     if (card && !card.locked) {
        //       if (pinboardStore.pickedCard?.card === card) {
        //         pinboardStore.pickPlacedCard(null);
        //       } else {
        //         pinboardStore.pickPlacedCard(card, { x: e.clientX, y: e.clientY });
        //       }
        //     }
        //   },
        //   disabled: card.locked,
        // },
        {
          label: pinboardStore.pinnedCard === card.uuid ? t('pinboard.actions.unpin') : t('pinboard.actions.pin'),
          onClick: (e) => {
            e.stopPropagation();
            if (card) {
              if (pinboardStore.pinnedCard === card.uuid) {
                pinboardStore.setPinnedCard(null);
              } else {
                pinboardStore.setPinnedCard(card.uuid);
              }
            }
          },
        },
        {
          label: pinboardStore.lockedCards.has(card.uuid) ? t('pinboard.actions.unlock') : t('pinboard.actions.lock'),
          onClick: () => {
            if (card) {
              if (pinboardStore.lockedCards.has(card.uuid)) pinboardStore.unlockCard(card.uuid);
              else pinboardStore.lockCard(card.uuid);
            }
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('rooms.title', { count: 2 }),
          tooltip: t('pinboard.actions.noRoomsHint'),
          disabled: true,
        },
        {
          label: t('pinboard.actions.singleHour'),
          onClick: async () => {
            await convertToSingle();
          },
          disabled: !!card.duration && card.duration <= 1,
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.remove'),
          onClick: () => {
            if (card) pinboardStore.discardPickedCard();
          },
          type: 'error',
          disabled: !card.startTimeGridEntry,
        },
      );

      if (onDelete) {
        menu.push({
          label: t('pinboard.actions.deleteCard'),
          onClick: () => onDelete(false),
          type: 'error',
        });
        if (count > 1) {
          menu.push({
            label: t('pinboard.actions.deleteAllCards', { count }),
            onClick: () => onDelete(true),
            type: 'error',
          });
        }
      }

      return menu;
    }, [card, count, hasWarnings, hasAvailabilityWarnings, hasRooms, pinboardStore, convertToSingle, onDelete]);

    const wrapperClasses = classNames(
      styles['pinboard-card'],
      {
        [styles['is-multi']]: count >= 1,
        [styles['is-picked']]: pinboardStore.pickedCard?.cardUuid === card.uuid,
        [styles['is-pinned']]: pinboardStore.pinnedCard === card.uuid,
        [styles['is-locked']]: pinboardStore.lockedCards.has(card.uuid),
      },
      className,
    );

    const menuClasses = classNames(styles.menu, {
      [styles['is-placed']]: !!card.startTimeGridEntry,
    });

    // Darstellung „Fachfarbe für Kärtchen": die Fachfarbe ersetzt die Lehrkraft-Streifen, und die
    // Textfarbe richtet sich nach dem, was tatsächlich hinter der Beschriftung liegt.
    const badgeColors = getBadgeCardColors({
      colorBySubject: pinboardStore.cardColorBySubject,
      subjectColor: card.subject?.timetableConfig?.color ?? null,
      rows: card.badgeCardRows,
      teacherTextColor: card.badgeCardTextColor,
    });

    const debounceOnCardHover = useDebounceCallback(() => {
      pinboardStore.setHoveredCard(card.uuid);
    }, 500);

    const onMouseEnter = () => {
      if (!pinboardStore.hoveredCard) {
        pinboardStore.setHoveredCard(card.uuid);
      } else {
        debounceOnCardHover();
      }
    };
    const onMouseLeave = () => {
      debounceOnCardHover.cancel();
    };

    return (
      <ContextMenu className={menuClasses} disabled={passive} data={contextMenu} usePortal={false}>
        <div
          className={wrapperClasses}
          title={hideTitle ? undefined : (card.subject?.name ?? '')}
          onClick={(e) => {
            onClick && onClick(e, card);
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <BadgeCard
            label={label ?? card.subject?.shortName ?? ''}
            width={width ?? (card.duration as BadgeCardWidth)}
            rows={badgeColors.rows}
            color={badgeColors.color}
            count={count}
            backgroundColor={badgeColors.backgroundColor}
            style={badgeColors.borderColor ? { outline: `1px solid ${badgeColors.borderColor}` } : undefined}
          />
        </div>
      </ContextMenu>
    );
  },
);
